"use client";
import React from "react";
import cn from "@/utils/cn";
import Image from "next/image";
import { useFormContext } from "react-hook-form";
import InputCrossIcon from "@/assets/icons/input-cross-icon.svg";
import { InputErrorIcon } from "@/assets/svg-icons/svg-icons";

type InputFieldProps = {
  name: string;
  isRequired?: boolean;
  subLabel?: string;
  errorTextPosition?: "right" | "top";
  showErrorInTooltip?: boolean;
  labelStyle?: React.CSSProperties;
  containerClassName?: string;
  successMessage?: string | null; // For external success messages
  apiError?: string | null; // For external API/form submission errors
} & React.InputHTMLAttributes<HTMLInputElement>;

export default function SignupInputField({
  name,
  className,
  isRequired = false,
  errorTextPosition = "right",
  containerClassName = "",
  successMessage: externalSuccessMessage,
  apiError: externalApiError,
  ...props
}: InputFieldProps) {
  const {
    watch,
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  const value = watch(name);
  const fieldValidationError = errors?.[name]?.message as string | undefined;

  let displayMessage: string | null = null;
  let messageIsError = false;

  // Determine which message to display and its type
  if (externalSuccessMessage && externalSuccessMessage.length > 0) {
    displayMessage = externalSuccessMessage;
    messageIsError = false;
  } else if (externalApiError && externalApiError.length > 0) {
    displayMessage = externalApiError;
    messageIsError = true;
  } else if (fieldValidationError && fieldValidationError.length > 0) {
    displayMessage = fieldValidationError; // Using the Zod message directly
    messageIsError = true;
  }

  const showMessage = displayMessage !== null;
  const hasErrorToDisplay = showMessage && messageIsError;
  const hasSuccessToDisplay = showMessage && !messageIsError;

  return (
    <div className={cn("w-full relative", containerClassName)}>
      {/* Error/Success message positioned absolutely above the input */}
      {hasErrorToDisplay && (
        <div className="absolute -top-2 left-2 z-10 flex items-start xl:-top-4">
          <div className="flex items-center">
            <InputErrorIcon className="max-xl:size-1.5" />
            <p className="ml-1 whitespace-nowrap text-[7px] text-[#FA5A7D] xl:text-xs">
              {displayMessage}
            </p>
          </div>
        </div>
      )}
      {hasSuccessToDisplay && (
        <div className="absolute -top-2 left-2 z-10 flex items-start xl:-top-4">
          <div className="flex items-center">
            <svg
              className="size-4 text-green-500"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5 13l4 4L19 7"></path>
            </svg>
            <p className="ml-1 whitespace-nowrap text-[7px] text-green-500 xl:text-xs">
              {displayMessage}
            </p>
          </div>
        </div>
      )}

      <div className="relative">
        <input
          id={name}
          className={cn(
            // Base styles from your original component
            "w-full bg-[#F9FAFB] rounded-[100px] pl-3 pr-9 md:pb-1.5 md:pt-1.5 py-1 relative text-[10.5px] xl:text-[14px] text-kd-secondary-1 placeholder:text-kd-secondary-3 font-normal focus:outline-none focus:ring-1 focus:ring-kd-lte-blue border border-kd-secondary-6",
            "h-10 w-full rounded-[10.56px] bg-[#F9FAFB] px-[24px] xl:h-[60px] xl:rounded-2xl",
            className, // Prop styles (e.g., from CTABanner)
            // Conditional error styling (should override border/ring from className or base)
            hasErrorToDisplay && "!border-[#FA5A7D] focus:ring-[#FA5A7D]",
          )}
          {...props}
          {...register(name)}
          value={value || ""}
        />

        {watch(name) &&
          props.type !== "password" && ( // Do not show cross for password fields
            <div
              onClick={() => {
                setValue(name, "");
              }}
              className="absolute inset-y-0 right-[16px] flex h-full cursor-pointer items-center justify-center"
            >
              <Image src={InputCrossIcon} alt="Clear input field" />
            </div>
          )}
      </div>
    </div>
  );
}
